/**
 * Receivables Aging (BE-14a)
 *
 * As of today: what each customer still owes, by the age of the invoice (current, 1-30, 31-60, 61-90, over 90 days).
 * Same report style as Customer Advances / Debtors; a row opens the customer's statement.
 * Route: /reports/receivables-aging
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, CircularProgress, Alert, Stack,
  Autocomplete, useMediaQuery, useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { exportService } from '../../services/accounting/export.service';
import { serverMessage } from '../../utils/apiConvert';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const BUCKETS = [
  { key: 'current', label: 'Current' },
  { key: 'days1To30', label: '1-30 days' },
  { key: 'days31To60', label: '31-60 days' },
  { key: 'days61To90', label: '61-90 days' },
  { key: 'daysOver90', label: '90+ days', warn: true },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

// The server's keys days_1_30 … arrive as days130 after camelCase conversion; read them by their snake names
const bucket = (row, key) => Number({
  current: row.current,
  days1To30: row.days130 ?? row.days_1_30,
  days31To60: row.days3160 ?? row.days_31_60,
  days61To90: row.days6190 ?? row.days_61_90,
  daysOver90: row.daysOver90 ?? row.days_over_90,
}[key] || 0);

const ReceivablesAging = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getReceivablesAging());
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the receivables aging'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Export with the page's own filters (BE-14b); the file comes back under the server's name
  const [exporting, setExporting] = useState(null);
  const doExport = async (format) => {
    setExporting(format);
    try {
      await exportService.download('receivables-aging', format, {});
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  const allRows = report?.rows || [];
  const rows = selectedCustomer ? allRows.filter((r) => r.customerId === selectedCustomer.id) : allRows;
  const totals = selectedCustomer ? rows[0] || {} : report?.totals || {};
  const customers = allRows.map((r) => ({ id: r.customerId, label: r.customerName }));
  const overColor = isDark ? '#ef5350' : '#c62828';

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {[...BUCKETS, { key: 'total', label: 'Total Outstanding' }].map((b) => {
        const value = b.key === 'total' ? Number(totals.total || 0) : bucket(totals, b.key);
        const warn = b.warn && value > 0;
        return (
          <Grid item xs={6} sm={4} md={2} key={b.key}>
            <Card sx={{ bgcolor: warn ? (isDark ? 'rgba(239,83,80,0.12)' : '#ffebee') : b.key === 'total' ? 'action.hover' : undefined, height: '100%' }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, mb: 0.5 }}>
                  {b.label}
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: warn ? overColor : 'text.primary' }}>
                  {formatCurrency(value)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  const cellSx = { fontSize: '13px', py: 1 };
  const headSx = { fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' };

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
            Receivables Aging
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            Unpaid invoices by age, as of {formatDate(report?.asOf)} — counted from the invoice date
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" variant="contained" startIcon={<ExcelIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('csv')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button size="small" variant="contained" startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('pdf')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button size="small" variant="outlined" onClick={fetchData} sx={{ minWidth: 36 }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Autocomplete size="small" options={customers}
              value={selectedCustomer}
              onChange={(e, v) => setSelectedCustomer(v)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Customer / Organization" />}
            />
          </Grid>
        </Grid>
      </Paper>

      {!loading && renderSummaryCards()}

      {/* Table / Cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No customer owes anything.</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <Card key={row.customerId} variant="outlined" onClick={() => navigate(`/reports/client-ledger/${row.customerId}`)} sx={{ cursor: 'pointer' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{row.customerName}</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700 }}>{formatCurrency(row.total)}</Typography>
                </Box>
                {BUCKETS.filter((b) => bucket(row, b.key) > 0).map((b) => (
                  <Box key={b.key} display="flex" justifyContent="space-between">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{b.label}</Typography>
                    <Typography sx={{ fontSize: fontSize.xs, fontWeight: 600, color: b.warn ? overColor : 'text.primary' }}>{formatCurrency(bucket(row, b.key))}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ ...headSx, width: 40 }}>#</TableCell>
                  <TableCell sx={headSx}>Customer Name</TableCell>
                  {BUCKETS.map((b) => <TableCell key={b.key} align="right" sx={{ ...headSx, color: b.warn ? overColor : headSx.color }}>{b.label}</TableCell>)}
                  <TableCell align="right" sx={headSx}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={row.customerId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/reports/client-ledger/${row.customerId}`)}>
                    <TableCell sx={{ ...cellSx, color: 'text.secondary' }}>{idx + 1}</TableCell>
                    <TableCell sx={cellSx}><Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{row.customerName}</Typography></TableCell>
                    {BUCKETS.map((b) => {
                      const v = bucket(row, b.key);
                      return (
                        <TableCell key={b.key} align="right" sx={{ ...cellSx, fontWeight: v > 0 ? 600 : 400, color: v > 0 ? (b.warn ? overColor : 'text.primary') : 'text.secondary' }}>
                          {v > 0 ? formatCurrency(v) : '-'}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: 700 }}>{formatCurrency(row.total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
                  <TableCell colSpan={2} sx={{ fontSize: '13px', fontWeight: 700, py: 1.5 }}>TOTALS ({rows.length} customers)</TableCell>
                  {BUCKETS.map((b) => (
                    <TableCell key={b.key} align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>{formatCurrency(bucket(totals, b.key))}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>{formatCurrency(totals.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ReceivablesAging;
